import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { updateProductStatus } from "../server/Product";
import showAlert from "../server/ShowAlert";
import {
  getProductStatusUpdateBlockers,
  resolveProductActiveStatus,
  resolveProductCategorySlug,
} from "../utils/productStatus";

/**
 * Product status change shared by catalog cards and admin tables.
 * The displayed status stays until the request finishes.
 * @param {(productId: string|number, status: string) => void} applyLocalStatus
 */
export const useProductStatusToggle = (applyLocalStatus) => {
  const stationId = useSelector((state) => state.user.stationId);
  const [statusPendingByProductId, setStatusPendingByProductId] = useState({});
  const statusPendingRef = useRef({});

  const setProductStatus = useCallback(
    async (product, categorySlug, nextStatus) => {
      const productId =
        product?.productId ?? product?.product_id ?? product?.id;
      const currentStatus = resolveProductActiveStatus(product);
      if (
        !currentStatus ||
        !nextStatus ||
        nextStatus === currentStatus ||
        productId == null ||
        productId === ""
      )
        return;

      const newStatus = nextStatus;
      const missing = getProductStatusUpdateBlockers(
        product,
        categorySlug,
        newStatus,
      );
      if (missing.length) {
        showAlert({
          msgTitle: `לא ניתן לעדכן סטטוס לפני הגדרת: ${missing.join(", ")}`,
          msgType: "error",
        });
        return;
      }

      const pendingKey = String(productId);
      if (statusPendingRef.current[pendingKey]) return;

      const slug = resolveProductCategorySlug(product, categorySlug);
      if (!slug) {
        showAlert({
          msgTitle: "לא נמצאה קטגוריה לעדכון הסטטוס",
          msgType: "error",
        });
        return;
      }

      statusPendingRef.current[pendingKey] = true;
      setStatusPendingByProductId((prev) => ({ ...prev, [pendingKey]: true }));

      try {
        await updateProductStatus(productId, slug, newStatus, stationId);
        applyLocalStatus?.(productId, newStatus);
        showAlert({
          msgTitle: "הסטטוס עודכן בהצלחה",
          msgType: "success",
        });
      } catch {
        showAlert({
          msgTitle: "אירעה שגיאה בעדכון הסטטוס",
          msgType: "error",
        });
      } finally {
        delete statusPendingRef.current[pendingKey];
        setStatusPendingByProductId((prev) => {
          const next = { ...prev };
          delete next[pendingKey];
          return next;
        });
      }
    },
    [applyLocalStatus, stationId],
  );

  return { setProductStatus, statusPendingByProductId };
};
